import { OMSView } from '@src/components/OMS/OMSView';
import { useFeatureFlag, useRoleAuth, useTransfersDrawer } from '@src/hooks';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import {
  ACTION,
  ButtonVariants,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceContext,
  NoteVariant,
  Popover,
  Text,
  Tour,
  useDisclosure,
  useIsMinWidth,
  useMixpanel,
  useNotes,
  usePopoverState,
} from '@talos/kyoko';
import { memo, useCallback, useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AutoHedgingButton } from '../../components/AutoHedging/AutoHedgingButton';
import { selectView, toggleView } from '../../components/OMS/OMSSlice';
import { isOrderFormView } from '../../components/OMS/viewsWithTabs';
import { useOrderFormToggle } from '../../hooks/useOrderFormToggle';
import { TourName } from '../../tokens/tours';
import type { LayoutSection } from '../../types/LayoutConfig';
import { TradingControlsDialog } from '../Admin/TradingControls/Dialog/TradingControlsDialog';
import { TradingControlsDialogButton } from '../Admin/TradingControls/Dialog/TradingControlsDialogButton';
import { useTradingControlsDialog } from '../Admin/TradingControls/Dialog/useTradingControlsDialog';
import { AppInboxHeaderButton } from './AppInboxHeaderButton';
import { HeaderButton } from './HeaderButton';
import { KillSwitchModal } from './KillSwitchModal';
import { LayoutMenu } from './LayoutMenu';
import { SupportMenuButton } from './SupportMenuButton';

const POPOVER_TARGET_STYLE = { height: '100%' };
const COMPACT_HEADER_BUTTONS_MIN_WIDTH = 1540;

export const HeaderButtons = memo(() => {
  const mixpanel = useMixpanel();
  const { getConfig } = useOrgConfiguration();
  const killSwitchModal = useDisclosure();
  const { addNote, removeNote } = useNotes();
  const isShowingNotification = useRef(false);
  const isTradingDisabled = getConfig(OrgConfigurationKey.TradingDisableAll, '0') === '1';
  const { enableMonitoringSpaces } = useFeatureFlag();
  const { toggleTransfersDrawer, closeTransfersDrawer, isTransfersDrawerOpen } = useTransfersDrawer();
  const { isAuthorized } = useRoleAuth();
  const isKillSwitchRole = isAuthorized(ACTION.TOGGLE_KILLSWITCH);
  const tradingControlsDialog = useTradingControlsDialog();
  const dispatch = useAppStateDispatch();
  const view = useAppStateSelector(selectView);
  const { enableAutoHedging, enableNotifications, enableHeaderSupportMenu } = useFeatureFlag();
  const isAppInboxVisible = enableNotifications;

  useEffect(() => {
    if (isTradingDisabled && !isShowingNotification.current) {
      addNote({
        id: 'trading-disabled',
        text: (
          <HStack gap="spacing8">
            <Icon color="colorTextPrimaryButton" icon={IconName.CloseCircle} />
            <Text color="colorTextPrimaryButton">Trading is currently disabled.</Text>
          </HStack>
        ),
        closable: false,
        variant: NoteVariant.Negative,
      });
      isShowingNotification.current = true;
    }
    if (!isTradingDisabled && isShowingNotification.current) {
      removeNote('trading-disabled', false);
      isShowingNotification.current = false;
    }
  }, [addNote, removeNote, isTradingDisabled]);

  const handleOrderToggle = useOrderFormToggle();

  const handleClickOpenOrders = useCallback(() => {
    closeTransfersDrawer(MixpanelEventSource.Header);
    dispatch(toggleView(OMSView.Cards));
    const shouldClose = view === OMSView.Cards;
    mixpanel.track(MixpanelEvent.ToggleActiveOrders, { [MixpanelEventProperty.Expanded]: !shouldClose });
  }, [closeTransfersDrawer, dispatch, view, mixpanel]);

  const handleTransfersToggle = useCallback(() => {
    toggleTransfersDrawer();
  }, [toggleTransfersDrawer]);

  const showExpandedButtons = useIsMinWidth(COMPACT_HEADER_BUTTONS_MIN_WIDTH);

  const canViewOrderEntry = [
    ACTION.VIEW_ORDER_FORM,
    ACTION.VIEW_RFQ_FORM,
    ACTION.VIEW_SALES_ORDER_FORM,
    ACTION.VIEW_SALES_RFQ_FORM,
  ].some(isAuthorized);

  const canViewTransfers = isAuthorized(ACTION.SUBMIT_TRANSFER);

  const viewOrdersAuthorized = isAuthorized(ACTION.VIEW_ORDER);

  const layoutMenu = usePopoverState({
    placement: 'bottom',
    closeOnClickOutside: true,
    noPaddingAndBorder: true,
    onOpen: () => {
      mixpanel.track(MixpanelEvent.OpenLayoutMenu);
    },
  });

  const orderFormVariant = !!view && isOrderFormView(view) ? ButtonVariants.Priority : ButtonVariants.Default;
  const transfersVariant = isTransfersDrawerOpen ? ButtonVariants.Priority : ButtonVariants.Default;
  const handleCloseKillSwitchModal = killSwitchModal.close;
  const handleOpenKillSwitchModal = killSwitchModal.open;

  const layoutMenuButton = useCallback(
    (layoutSection: LayoutSection) => (
      <Popover {...layoutMenu} targetStyle={POPOVER_TARGET_STYLE}>
        <HeaderButton
          data-testid="layout-menu-button"
          borderPlacement="left"
          startIcon={IconName.Template}
          compact={!showExpandedButtons}
          tooltip="Layout Menu"
          variant={layoutMenu.isOpen ? ButtonVariants.Priority : ButtonVariants.Default}
          dim
        />
        <MixpanelSourceContext.Provider value={MixpanelEventSource.LayoutMenu}>
          <LayoutMenu close={layoutMenu.close} layoutSection={layoutSection} additionalContentSections={[]} />
        </MixpanelSourceContext.Provider>
      </Popover>
    ),
    [layoutMenu, showExpandedButtons]
  );

  return (
    <>
      {enableAutoHedging && <AutoHedgingButton compact={!showExpandedButtons} />}
      {isKillSwitchRole && !tradingControlsDialog.enabled && (
        <HeaderButton
          ghost
          borderPlacement="left"
          variant={ButtonVariants.Negative}
          size={FormControlSizes.Tiny}
          onClick={handleOpenKillSwitchModal}
          startIcon={showExpandedButtons && !isTradingDisabled ? IconName.LockOpen : IconName.ExclamationCircleSolid}
          compact={!showExpandedButtons}
          dim
        >
          {!isTradingDisabled ? 'Disable' : 'Re-enable'} Trading
        </HeaderButton>
      )}
      <TradingControlsDialogButton {...tradingControlsDialog} compact={!showExpandedButtons} />
      {enableHeaderSupportMenu && <SupportMenuButton compact={!showExpandedButtons} />}
      {canViewTransfers && (
        <>
          <Tour tour={TourName.v50TransfersHeaderButton} />
          <HeaderButton
            borderPlacement="left"
            onClick={handleTransfersToggle}
            size={FormControlSizes.Small}
            variant={transfersVariant}
            startIcon={IconName.ArrowLeftRight}
            data-testid="header-button-transfers"
            compact={!showExpandedButtons}
            dim
          >
            Transfers
          </HeaderButton>
        </>
      )}
      {canViewOrderEntry && (
        <HeaderButton
          borderPlacement="left"
          onClick={handleOrderToggle}
          size={FormControlSizes.Small}
          variant={orderFormVariant}
          startIcon={IconName.TrendUp}
          data-testid="header-button-order-form"
          dim
        >
          Order Form
        </HeaderButton>
      )}
      {viewOrdersAuthorized && (
        <HeaderButton
          borderPlacement="left"
          onClick={handleClickOpenOrders}
          size={FormControlSizes.Small}
          variant={view === OMSView.Cards ? ButtonVariants.Priority : ButtonVariants.Default}
          startIcon={IconName.Collection}
          data-testid="header-button-orders"
          compact={!showExpandedButtons}
          dim
        >
          Orders
        </HeaderButton>
      )}
      {isAppInboxVisible && <AppInboxHeaderButton compact={!showExpandedButtons} />}
      <Routes>
        <Route path="/trading/*" element={layoutMenuButton('trading')} />
        {enableMonitoringSpaces && <Route path="/monitoring/*" element={layoutMenuButton('monitoring')} />}
      </Routes>
      <KillSwitchModal isOpen={killSwitchModal.isOpen} onClose={handleCloseKillSwitchModal} />
      <TradingControlsDialog {...tradingControlsDialog} />
    </>
  );
});
